import { FC, useMemo, useState } from 'react';
import styles from './Tabs.module.scss';
import { useHistory, useLocation } from 'react-router-dom';
import { EVENTS_ROOT, ROUTES } from '../../shared/routes';
import { CopyClipboard, Tabs as WixTabs } from 'wix-style-react';
import { useAppSelector } from 'src/hooks';
import { AttendeeQuotaAmount } from 'src/redesign/features/PlanInformation';
import { getSelectedSiteDataSelector } from 'src/redesign/features/SelectedSite/store/selectors';
import { RedesignButton } from '@components/RedesignButton';
import { Text } from '@components/Text';
import {
  hasRequestEventCreationRoleSelector,
  isAdminOrOwnerSelector,
} from 'src/redesign/features/Account/store/selectors';
import { Loader } from '@components/Loader';
import { GlobalAttendeeSearchModal } from 'src/redesign/components/GlobalAttendeeSearchModal';

const ALL_TABS = [
  {
    id: EVENTS_ROOT,
    title: 'Events',
  },
  {
    id: ROUTES.analytics,
    title: 'Analytics',
  },
  {
    id: ROUTES.design,
    title: 'Design',
  },
  {
    id: ROUTES.integrations,
    title: 'Integrations',
  },
  {
    id: ROUTES.siteSettings,
    title: 'Site Settings',
  },
  {
    id: ROUTES.team,
    title: 'Invite Team',
  },
];

const DESIGN_PATHS = [
  `${ROUTES.design}/brand`,
  `${ROUTES.design}/site`,
  `${ROUTES.design}/event`,
  `${ROUTES.design}/widget`,
  `${ROUTES.design}/ticket`,
  `${ROUTES.design}/communication`,
];

const AVAILABLE_PATH = [`${EVENTS_ROOT}/`, EVENTS_ROOT, ...Object.values(ROUTES), ...DESIGN_PATHS];

export const Tabs: FC = () => {
  const history = useHistory();
  const location = useLocation();
  const currentSite = useAppSelector(getSelectedSiteDataSelector);
  const isAdminOrOwner = useAppSelector(isAdminOrOwnerSelector);
  const hasRequestEventCreationRole = useAppSelector(hasRequestEventCreationRoleSelector);
  const [attendeeSearchModalVisible, setAttendeeSearchModalVisible] = useState(false);

  // Filter tabs based on user role
  const TABS = useMemo(() => {
    if (isAdminOrOwner) {
      return ALL_TABS; // Admin and Owner see all tabs
    } else if (hasRequestEventCreationRole) {
      // Users with REQUEST_EVENT_CREATION role can only see Events tab
      return ALL_TABS.filter((tab) => tab.id === EVENTS_ROOT);
    }
    return ALL_TABS; // Default fallback to all tabs
  }, [isAdminOrOwner, hasRequestEventCreationRole]);

  const handleTabClick = (link: string) => {
    const hasSiteParam = /:site-id/.exec(link);
    if (hasSiteParam) {
      history.replace(`${link.replace(/:site-id/, currentSite.id)}${location.search}`);
      return;
    }

    history.push({ pathname: link, search: location.search });
  };

  AVAILABLE_PATH.push(`/sites/${currentSite?.id}/team`);

  const activeTab = useMemo(() => {
    if (/team/.exec(location.pathname)) return '/sites/:site-id/team';
    if (/design/.exec(location.pathname)) return ROUTES.design;
    return location.pathname;
  }, [location.pathname]);

  // Check if current path is valid - support design subroutes like /design/communication/:type
  const isValidPath =
    AVAILABLE_PATH.includes(location.pathname) ||
    location.pathname.startsWith(ROUTES.design) ||
    location.pathname.startsWith(EVENTS_ROOT);

  if (!isValidPath) return null;

  if (!currentSite) {
    return <Loader size="small" statusMessage="Loading Site Information" />;
  }
  return (
    <div className={styles.tabsWrapper}>
      <nav className={styles.wrapper}>
        <div className={styles.siteInformation}>
          <span className={styles.siteName}>{currentSite?.site_display_name}</span>
          <div className={styles.controls}>
            <AttendeeQuotaAmount />
            <CopyClipboard
              value={
                currentSite?.site_page_url || `https://ticketspotapp.com/${currentSite?.subdomain}`
              }
            >
              {({ copyToClipboard, isCopied }) => {
                return (
                  <RedesignButton
                    text
                    className={styles.eventSiteUrlButton}
                    onClick={() => {
                      copyToClipboard && copyToClipboard();
                    }}
                  >
                    <Text color="brand-400" bold>
                      {isCopied ? 'Copied!' : 'Event Site URL'}
                    </Text>

                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clipPath="url(#clip0_1_7135)">
                        <path
                          d="M4.1665 12.5001H3.33317C2.89114 12.5001 2.46722 12.3245 2.15466 12.0119C1.8421 11.6994 1.6665 11.2754 1.6665 10.8334V3.33341C1.6665 2.89139 1.8421 2.46746 2.15466 2.1549C2.46722 1.84234 2.89114 1.66675 3.33317 1.66675H10.8332C11.2752 1.66675 11.6991 1.84234 12.0117 2.1549C12.3242 2.46746 12.4998 2.89139 12.4998 3.33341V4.16675M9.1665 7.50008H16.6665C17.587 7.50008 18.3332 8.24627 18.3332 9.16675V16.6667C18.3332 17.5872 17.587 18.3334 16.6665 18.3334H9.1665C8.24603 18.3334 7.49984 17.5872 7.49984 16.6667V9.16675C7.49984 8.24627 8.24603 7.50008 9.1665 7.50008Z"
                          stroke="#9D25DC"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_1_7135">
                          <rect width="20" height="20" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </RedesignButton>
                );
              }}
            </CopyClipboard>
            <RedesignButton
              text
              className={styles.searchAttendeesButton}
              onClick={() => setAttendeeSearchModalVisible(true)}
            >
              <Text color="brand-400" bold>
                Search
              </Text>
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 17C13.4183 17 17 13.4183 17 9C17 4.58172 13.4183 1 9 1C4.58172 1 1 4.58172 1 9C1 13.4183 4.58172 17 9 17Z"
                  stroke="#9D25DC"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M19 19L14.65 14.65"
                  stroke="#9D25DC"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </RedesignButton>
          </div>
        </div>
        <WixTabs
          className={styles.tabs}
          items={TABS}
          onClick={(item) => handleTabClick(item.id as string)}
          activeId={activeTab}
          hasDivider={false}
        />
      </nav>
      <GlobalAttendeeSearchModal
        visible={attendeeSearchModalVisible}
        onHide={() => setAttendeeSearchModalVisible(false)}
      />
    </div>
  );
};
